/**
 * Configuración › Republicación y denuncias (2026-09-18).
 * Bloque 1: avisos que Mercado Libre cierra por 45 días sin ventas → republicar (padre) o
 *           recrear el país desde el padre (hijo).
 * Bloque 2: denuncias PPPI → retiro automático ("RETIRO VOLUNTARIO") y recreación del hijo a los N días.
 * API: /api/mercadolibre/relist (permiso settings.relist). Parámetros en automation_config.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Play, Check } from 'lucide-react';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';

type Key = 'ml_relist' | 'ip_complaints_auto';

interface Automation {
  enabled: boolean;
  settings: Record<string, unknown>;
  last_run_at: string | null;
  last_run_stats: Record<string, unknown> | null;
}

interface Summary {
  ml_relist: Automation;
  ip_complaints_auto: Automation;
  last_24h: Array<{ path: string; decision: string; n: number }>;
  last_7d: Array<{ path: string; decision: string; n: number }>;
}

interface LogRow {
  id: number;
  path: string;
  asin: string | null;
  cbt_item_id: string | null;
  site_id: string | null;
  target_item_id: string | null;
  decision: string;
  reason: string | null;
  dry_run: boolean;
  new_item_id: string | null;
  created_at: string;
}

const PATH_LABEL: Record<string, string> = { parent: 'Padre cerrado', child: 'País cerrado', complaint: 'Denuncia' };
const DECISION_LABEL: Record<string, string> = {
  requeued: 'Re-encolado', done: 'Republicado', discarded: 'Descartado', failed: 'Falló',
  would_requeue: 'Republicaría (solo mirar)', would_recreate: 'Recrearía (solo mirar)',
  would_withdraw: 'Retiraría (solo mirar)', withdrawn: 'Retirado', withdraw_partial: 'Retiro parcial',
};
const decisionClass = (d: string) =>
  d === 'done' || d === 'requeued' || d === 'withdrawn' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
  : d.startsWith('would_') ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
  : d === 'failed' || d === 'withdraw_partial' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';

function Toggle({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${checked ? 'bg-[var(--color-primary)]' : 'bg-gray-300 dark:bg-gray-600'} disabled:opacity-50`}>
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  );
}

const fmt = (s: string | null) => (s ? new Date(s).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const CAP_OPTIONS = [{ v: 0, l: 'Indefinido' }, { v: 1, l: '1 vez' }, { v: 2, l: '2 veces' }, { v: 3, l: '3 veces' }, { v: 4, l: '4 veces' }, { v: 5, l: '5 veces' }];

export function RelistTab() {
  const { sellerId } = useUIStore();
  const [data, setData] = useState<Summary | null>(null);
  const [log, setLog] = useState<LogRow[]>([]);
  const [pathFilter, setPathFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [running, setRunning] = useState<Key | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, l] = await Promise.all([
      api.get<Summary>(`/mercadolibre/relist/summary?seller_id=${sellerId}`),
      api.get<LogRow[]>(`/mercadolibre/relist/log?seller_id=${sellerId}&limit=150${pathFilter ? `&path=${pathFilter}` : ''}`),
    ]);
    if (s.success && s.data) setData(s.data as unknown as Summary);
    if (l.success && l.data) setLog(l.data as unknown as LogRow[]);
    setLoading(false);
  }, [sellerId, pathFilter]);

  useEffect(() => { load(); }, [load]);

  const save = async (key: Key, patch: { enabled?: boolean; settings?: Record<string, unknown> }, field: string) => {
    setSaving(`${key}.${field}`);
    const res = await api.put(`/mercadolibre/relist/config?seller_id=${sellerId}`, { seller_id: sellerId, key, ...patch });
    setSaving(null);
    if (!res.success) { setNotice(`No se pudo guardar: ${(res as { error?: string }).error || 'error'}`); return; }
    setFlash(`${key}.${field}`); setTimeout(() => setFlash(null), 1500);
    load();
  };

  const runNow = async (key: Key) => {
    setRunning(key);
    const res = await api.post(`/mercadolibre/relist/run?seller_id=${sellerId}`, { seller_id: sellerId, key });
    setRunning(null);
    setNotice(res.success ? 'Corrida iniciada. Los resultados aparecen en la tabla en unos minutos.' : `No arrancó: ${(res as { error?: string }).error || 'error'}`);
    setTimeout(load, 8000);
  };

  const count = (rows: Summary['last_24h'] | undefined, pred: (r: { path: string; decision: string }) => boolean) =>
    (rows ?? []).filter(pred).reduce((a, r) => a + r.n, 0);

  if (loading && !data) return <div className="flex justify-center py-8 text-[var(--color-text-muted)]"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!data) return null;
  const relist = data.ml_relist; const auto = data.ip_complaints_auto;
  const Saved = ({ k }: { k: string }) => saving === k ? <Loader2 className="w-3 h-3 animate-spin inline ml-1" /> : flash === k ? <span className="text-xs text-green-600 ml-1 inline-flex items-center gap-0.5"><Check className="w-3 h-3" /> Guardado</span> : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-[var(--color-text)]">Republicación y denuncias</h2>
        <p className="text-xs text-[var(--color-text-muted)] mt-1">
          Todo cambio se guarda al instante y aplica a la cuenta seleccionada. Antes de actuar, cada caso se confirma en vivo con Mercado Libre.
        </p>
      </div>
      {notice && (
        <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 text-sm text-blue-900 dark:text-blue-200 flex justify-between gap-3">
          <span>{notice}</span><button onClick={() => setNotice(null)} className="text-xs underline">cerrar</button>
        </div>
      )}

      {/* Bloque 1 — cierres de ML */}
      <section className="border border-[var(--color-border)] rounded-lg p-4 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text)]">Avisos que Mercado Libre cierra por 45 días sin ventas</h3>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              Cada 2 horas revisa los cierres de los últimos {String(relist.settings.lookback_days ?? 7)} días. Si cerró el aviso padre, lo vuelve a encolar en el publicador con los países donde estaba vivo (y le pasa el stock propio si lo tenía). Si cerró solo un país y el padre sigue activo, crea ese país desde el padre.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs text-[var(--color-text-secondary)]">{relist.enabled ? 'Encendido' : 'Apagado'}</span>
            <Toggle checked={relist.enabled} onChange={(v) => save('ml_relist', { enabled: v }, 'enabled')} label="Republicación automática" />
            <Saved k="ml_relist.enabled" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <label className="flex items-center justify-between gap-2 p-3 rounded border border-[var(--color-border)]">
            <span>Solo mirar (registra sin republicar)</span>
            <span className="flex items-center"><Toggle checked={relist.settings.dry_run !== false} onChange={(v) => save('ml_relist', { settings: { dry_run: v } }, 'dry')} label="Solo mirar" /><Saved k="ml_relist.dry" /></span>
          </label>
          <label className="flex items-center justify-between gap-2 p-3 rounded border border-[var(--color-border)]">
            <span>Días mínimos publicado</span>
            <span className="flex items-center gap-1">
              <input type="number" min={1} defaultValue={Number(relist.settings.min_days_listed ?? 40)}
                onBlur={(e) => save('ml_relist', { settings: { min_days_listed: Number(e.target.value) || 40 } }, 'min')}
                className="w-20 px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)]" />
              <Saved k="ml_relist.min" />
            </span>
          </label>
          <label className="flex items-center justify-between gap-2 p-3 rounded border border-[var(--color-border)]">
            <span>Republicar cada producto hasta</span>
            <span className="flex items-center gap-1">
              <select value={Number(relist.settings.max_relists ?? 0) || 0}
                onChange={(e) => save('ml_relist', { settings: { max_relists: Number(e.target.value) || null } }, 'max')}
                className="px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)]">
                {CAP_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <Saved k="ml_relist.max" />
            </span>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--color-text-secondary)]">
          <span>Última corrida: {fmt(relist.last_run_at)}{relist.last_run_stats ? ` · ${JSON.stringify(relist.last_run_stats).slice(0, 160)}` : ''}</span>
          <span>Últimas 24 h: {count(data.last_24h, (r) => r.path !== 'complaint' && (r.decision === 'done' || r.decision === 'requeued'))} republicados · {count(data.last_24h, (r) => r.path !== 'complaint' && r.decision.startsWith('would_'))} que republicaría · {count(data.last_24h, (r) => r.path !== 'complaint' && r.decision === 'discarded')} descartados</span>
          <button onClick={() => runNow('ml_relist')} disabled={running === 'ml_relist'}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50">
            {running === 'ml_relist' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Correr ahora
          </button>
        </div>
      </section>

      {/* Bloque 2 — denuncias */}
      <section className="border border-[var(--color-border)] rounded-lg p-4 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text)]">Denuncias de propiedad intelectual</h3>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              Al llegar una denuncia, responde "RETIRO VOLUNTARIO", pausa y cierra el aviso denunciado (verificado en vivo). Cuando Mercado Libre cierra el caso, espera los días configurados y vuelve a crear ese país desde el padre.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs text-[var(--color-text-secondary)]">{auto.enabled ? 'Encendido' : 'Apagado'}</span>
            <Toggle checked={auto.enabled} onChange={(v) => save('ip_complaints_auto', { enabled: v }, 'enabled')} label="Denuncias automáticas" />
            <Saved k="ip_complaints_auto.enabled" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <label className="flex items-center justify-between gap-2 p-3 rounded border border-[var(--color-border)]">
            <span>Solo mirar</span>
            <span className="flex items-center"><Toggle checked={auto.settings.dry_run !== false} onChange={(v) => save('ip_complaints_auto', { settings: { dry_run: v } }, 'dry')} label="Solo mirar" /><Saved k="ip_complaints_auto.dry" /></span>
          </label>
          <label className="flex items-center justify-between gap-2 p-3 rounded border border-[var(--color-border)]">
            <span>Retirar al llegar</span>
            <span className="flex items-center"><Toggle checked={auto.settings.auto_withdraw !== false} onChange={(v) => save('ip_complaints_auto', { settings: { auto_withdraw: v } }, 'aw')} label="Retirar automáticamente" /><Saved k="ip_complaints_auto.aw" /></span>
          </label>
          <label className="flex items-center justify-between gap-2 p-3 rounded border border-[var(--color-border)]">
            <span>Días tras el cierre del caso</span>
            <span className="flex items-center gap-1">
              <input type="number" min={0} defaultValue={Number(auto.settings.republish_after_days ?? 3)}
                onBlur={(e) => save('ip_complaints_auto', { settings: { republish_after_days: Number(e.target.value) || 3 } }, 'days')}
                className="w-16 px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)]" />
              <Saved k="ip_complaints_auto.days" />
            </span>
          </label>
          <label className="flex items-center justify-between gap-2 p-3 rounded border border-[var(--color-border)]">
            <span>Recrear cada producto hasta</span>
            <span className="flex items-center gap-1">
              <select value={Number(auto.settings.max_republish ?? 0) || 0}
                onChange={(e) => save('ip_complaints_auto', { settings: { max_republish: Number(e.target.value) || null } }, 'max')}
                className="px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)]">
                {CAP_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <Saved k="ip_complaints_auto.max" />
            </span>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--color-text-secondary)]">
          <span>Última corrida: {fmt(auto.last_run_at)}{auto.last_run_stats ? ` · ${JSON.stringify(auto.last_run_stats).slice(0, 160)}` : ''}</span>
          <span>Últimas 24 h: {count(data.last_24h, (r) => r.path === 'complaint' && r.decision === 'withdrawn')} retirados · {count(data.last_24h, (r) => r.path === 'complaint' && r.decision === 'done')} recreados · {count(data.last_24h, (r) => r.path === 'complaint' && r.decision.startsWith('would_'))} en solo mirar</span>
          <button onClick={() => runNow('ip_complaints_auto')} disabled={running === 'ip_complaints_auto'}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50">
            {running === 'ip_complaints_auto' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Correr ahora
          </button>
        </div>
      </section>

      {/* Tabla de casos */}
      <section className="border border-[var(--color-border)] rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-[var(--color-border)]">
          <h3 className="text-sm font-semibold text-[var(--color-text)]">Casos recientes</h3>
          <select value={pathFilter} onChange={(e) => setPathFilter(e.target.value)}
            className="text-xs px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)]">
            <option value="">Todos</option>
            <option value="parent">Padre cerrado</option>
            <option value="child">País cerrado</option>
            <option value="complaint">Denuncias</option>
          </select>
        </div>
        {log.length === 0 ? (
          <div className="p-6 text-center text-sm text-[var(--color-text-muted)]">Todavía no hay casos registrados para esta cuenta.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[var(--color-text-secondary)] bg-[var(--color-bg)]">
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-left px-3 py-2">Camino</th>
                  <th className="text-left px-3 py-2">Producto</th>
                  <th className="text-left px-3 py-2">País</th>
                  <th className="text-left px-3 py-2">Resultado</th>
                  <th className="text-left px-3 py-2">Detalle</th>
                  <th className="text-left px-3 py-2">Aviso nuevo</th>
                </tr>
              </thead>
              <tbody>
                {log.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--color-border)]">
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{fmt(r.created_at)}</td>
                    <td className="px-3 py-2 text-xs">{PATH_LABEL[r.path] ?? r.path}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.asin ?? '—'}<div className="text-[10px] text-[var(--color-text-muted)]">{r.target_item_id}</div></td>
                    <td className="px-3 py-2 text-xs">{r.site_id ?? 'todos'}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs font-medium ${decisionClass(r.decision)}`}>{DECISION_LABEL[r.decision] ?? r.decision}</span>{r.dry_run && <span className="ml-1 text-[10px] text-[var(--color-text-muted)]">solo mirar</span>}</td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-secondary)] max-w-md truncate" title={r.reason ?? ''}>{r.reason ?? ''}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.new_item_id ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
